// Inline reminder; no modal, overlay, or additional scrolling container.
import { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import { enablePushNotifications, getPushPermissionState } from '../lib/pushNotifications';
import { PUSH_PROMPT_EVENT, setPushPromptAccount, clearPushPromptAccount,
  eligiblePushReminder, pushReminderCurrent, markDismissed, consumePushReminder,
  askOnMyFeed, recordPushReminderShown } from '../lib/pushPromptManager';
export function PushReminderSlot({ blocked = false }) {
  useEffect(() => { window.dispatchEvent(new Event(PUSH_PROMPT_EVENT)); }, [blocked]);
  return <div data-push-reminder-slot data-push-reminder-blocked={blocked ? "true" : "false"} />;
}

export default function PushPermissionSheet() {
  const { user, token } = useContext(AuthContext) || {};
  const location = useLocation();
  const serial = useRef(0);
  const enabling = useRef(false);
  const [reminder, setReminder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  useEffect(() => {
    setPushPromptAccount(user?.id, token);
    return clearPushPromptAccount;
  }, [user?.id, token]);
  useEffect(() => {
    let timer;
    const evaluate = () => {
      const request = ++serial.current;
      setReminder(null); setMessage(''); setBusy(false); enabling.current = false; clearTimeout(timer);
      timer = setTimeout(async () => {
        const slot = document.querySelector('[data-push-reminder-slot]');
        if (!slot || slot.dataset.pushReminderBlocked === 'true' || document.visibilityState !== 'visible') return;
        const next = await eligiblePushReminder(location.pathname === '/dashboard');
        if (request === serial.current && next && pushReminderCurrent(next)) { recordPushReminderShown(next); setReminder({ ...next, slot }); }
      }, 600);
    };
    const foreground = () => { if (!enabling.current && document.visibilityState === 'visible') evaluate(); };
    window.addEventListener(PUSH_PROMPT_EVENT, evaluate);
    window.addEventListener('focus', foreground);
    document.addEventListener('visibilitychange', foreground);
    if (location.pathname === '/dashboard' && document.querySelector('[data-push-reminder-slot]')) askOnMyFeed();
    evaluate();
    return () => { ++serial.current; clearTimeout(timer); window.removeEventListener(PUSH_PROMPT_EVENT, evaluate); window.removeEventListener('focus', foreground); document.removeEventListener('visibilitychange', foreground); };
  }, [user?.id, token, location.key, location.pathname]);
  if (!reminder || !pushReminderCurrent(reminder) || !reminder.slot.isConnected) return null;
  const enable = async () => {
    if (enabling.current) return;
    const started = serial.current;
    const current = () => serial.current === started && pushReminderCurrent(reminder);
    enabling.current = true; setBusy(true);
    try {
      const permission = await getPushPermissionState();
      if (!current()) return;
      if (permission === 'granted') { consumePushReminder(reminder); setReminder(null); return; }
      if (permission === 'denied') {
        setMessage('Open iPhone Settings → Notifications → Open Runs USA, then turn on Allow Notifications.'); return;
      }
      if (permission !== 'prompt') { setMessage('Notification permission could not be checked. Open Notification Settings to retry.'); return; }
      const result = await enablePushNotifications({ isCurrent: current });
      if (!current()) return;
      if (result.code === 'registered') { consumePushReminder(reminder); setReminder(null); }
      else if (result.code === 'denied') setMessage('Open iPhone Settings → Notifications → Open Runs USA, then turn on Allow Notifications.');
      else setMessage('Open Notification Settings for registration status and recovery guidance.');
    } finally { if (current()) { enabling.current = false; setBusy(false); } }
  };
  return createPortal(
    <aside className="mb-4 rounded-xl border border-orange-100 bg-orange-50 p-4" aria-label="Notification reminder" data-testid="push-permission-sheet">
      <h2 className="text-base font-semibold text-zinc-900">Don’t miss your next run</h2>
      <p className="mt-1 text-sm text-zinc-600">Get notified when you’re invited to a game or training, or when plans change.</p>
      {message && <p role="status" className="mt-2 text-sm">{message}</p>}
      <div className="mt-3 flex flex-wrap gap-3">
        <button type="button" onClick={enable} disabled={busy} className="min-h-11 rounded-lg bg-orange-500 px-4 text-sm font-semibold text-white" data-testid="push-permission-enable-btn">{busy ? 'Enabling…' : 'Enable Notifications'}</button>
        <button type="button" disabled={busy} onClick={() => { markDismissed(reminder); setReminder(null); }} className="min-h-11 px-3 text-sm text-zinc-600" data-testid="push-permission-not-now-btn">Not Now</button>
      </div>
    </aside>, reminder.slot);
}
